import { useCallback, useEffect, useMemo, useState } from "react";
import TopPageTitle from "../../../components/TopPageTitle/TopPageTitle";
import { PagesWrapper } from "../../../styles/components/PagesWrapper.styled";
import { getAllStudents } from "../../../helpers/getAllStudents";
import axios from "../../../axios";
import * as S from "./Funnel.styled";

// Воронка — мінімум (рішення Слави 27.10): Написав → Пробний → Навчається, або «Не зайшов» з причиною.
// Пробні з «Пробні уроки» потрапляють сюди самі; написали в директ — додаємо кнопкою «+ Людина».
// Бекенд зберігає детальні етапи (contacted/price/trial_done) — тут вони показуються як найближчий простий крок.
// Дані: GET /admin/funnel. Звітний період школи — з 9-го по 8-ме, рахуємо людей, які написали за період.

const SOURCE_LABEL = { instagram: "Instagram", tiktok: "TikTok", telegram: "Telegram", threads: "Threads", website: "Сайт", referral: "Рекомендація", google: "Google", ai: "ChatGPT / AI", other: "Інше" };
const STEPS = ["new", "contacted", "price", "trial_booked", "trial_done", "paid"];
const STEP_LABEL = { new: "Написав", contacted: "Відповіли", price: "Питав ціну", trial_booked: "Записався на пробний", trial_done: "Прийшов на пробний", paid: "Став учнем" };
const STEP_SHORT = { new: "Написав", contacted: "Відповіли", price: "Ціна", trial_booked: "Записався", trial_done: "Прийшов", paid: "Учень" };
const LOST_REASONS = ["Не прийшов на пробний", "Дорого", "Немає часу / не зараз", "Не сподобалось", "Зник, не відповідає", "Обрав іншу школу", "Інше"];
// Прості кроки: який детальний етап бекенду відповідає якому кроку
const SIMPLE = [
  { id: "new", label: "Написав", stages: ["new", "contacted", "price"] },
  { id: "trial_booked", label: "Пробний", stages: ["trial_booked", "trial_done"] },
  { id: "paid", label: "Навчається", stages: ["paid"] },
];
const simpleOf = (stage) => SIMPLE.find((x) => x.stages.includes(stage || "new"))?.id || "new";
const WHY_LABEL = { no_reply: ["не відповіли", "red"], missed_trial: ["не прийшов на пробний", "red"], stale: ["завис", "amber"], no_source: ["вкажи, звідки", "amber"] };
const TRIAL_LABEL = { scheduled: ["пробний призначено", "violet"], attended: ["був на пробному", "green"], joined: ["зайшов на навчання", "green"], missed: ["не прийшов", "red"], cancelled: ["пробний скасовано", "red"] };
const BY_LABEL = { teacher: "Викладач", student: "Учень", school: "Школа" };

const day = (d) => (d ? new Date(d).toLocaleDateString("uk-UA", { day: "numeric", month: "short" }) : "—");
const daysAgo = (d) => Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function periodStart(offset) {
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth() - (now.getDate() < 9 ? 1 : 0), 9);
  return new Date(base.getFullYear(), base.getMonth() + offset, 9);
}
const pct = (a, b) => (b ? `${Math.round((a / b) * 100)}%` : "—");
const withId = (name, id) => (id ? `${name} (ID ${id})` : name || "—");

function Funnel() {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [students, setStudents] = useState([]);
  const [filter, setFilter] = useState("active");

  const from = periodStart(offset);
  const to = periodStart(offset + 1);

  const load = useCallback(() => {
    setError("");
    axios
      .get(`admin/funnel?from=${iso(from)}&to=${iso(to)}`)
      .then((res) => setData(res.data))
      .catch(() => setError("Не вдалося завантажити воронку"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getAllStudents().then((list) => setStudents(list.filter((s) => !s.isPaused))).catch(() => {}); }, []);

  const showLostAt = (stage) => {
    setFilter(`lost:${stage}`);
    document.getElementById("funnel-people")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <PagesWrapper>
      <TopPageTitle value={"Воронка"} />
      <S.Page>
        <S.PeriodBar>
          <S.RoundBtn onClick={() => setOffset(offset - 1)} aria-label="Попередній період">←</S.RoundBtn>
          <span>Період {day(from)} – {day(new Date(to.getTime() - 86400000))}</span>
          <S.RoundBtn onClick={() => setOffset(offset + 1)} disabled={offset >= 0} aria-label="Наступний період">→</S.RoundBtn>
        </S.PeriodBar>
        {error && <S.ErrorText>{error}</S.ErrorText>}
        {data && (
          <>
            <Steps steps={data.steps} onLostClick={showLostAt} />
            <Leads leads={data.leads} filter={filter} setFilter={setFilter} onChange={load} />
            <S.Row2>
              <Sources bySource={data.bySource} />
              <LostReasons reasons={data.lostReasons} leads={data.leads} />
            </S.Row2>
            <details>
              <summary style={{ cursor: "pointer", fontWeight: 700, padding: "6px 0" }}>Продовження, «Приведи друга», відміни уроків</summary>
              <S.Page style={{ marginTop: 12 }}>
                <S.Row2>
                  <Retention r={data.retention} />
                  <Referrals items={data.referrals} onChange={load} />
                </S.Row2>
                <Cancellations c={data.cancelStats} items={data.cancellations} students={students} onChange={load} />
              </S.Page>
            </details>
          </>
        )}
      </S.Page>
    </PagesWrapper>
  );
}

// Скільки людей з тих, хто написав за період, дійшли до пробного і до навчання; скільки не зайшли
function Steps({ steps, onLostClick }) {
  const at = (stage) => steps.find((x) => x.stage === stage)?.reached || 0;
  const total = at("new");
  const lost = steps.reduce((n, x) => n + x.lostHere, 0);
  const cells = [
    { label: "Написали", n: total },
    { label: "Були на пробному / записані", n: at("trial_booked"), prev: total },
    { label: "Навчаються", n: at("paid"), prev: at("trial_booked") },
  ];
  return (
    <S.Card>
      <S.CardHead>
        <div>
          <S.H>Воронка</S.H>
          <S.Hint>Люди, які написали за цей період. Відсоток — від попереднього кроку.</S.Hint>
        </div>
      </S.CardHead>
      <S.Steps>
        {cells.map((c) => (
          <S.Step key={c.label} style={{ cursor: "default" }}>
            <S.Big>{c.n}</S.Big>
            <S.Small><b>{c.label}</b></S.Small>
            {c.prev !== undefined && <S.Rate>{pct(c.n, c.prev)}</S.Rate>}
          </S.Step>
        ))}
        <S.Step as="button" type="button" onClick={() => onLostClick("any")} disabled={!lost} style={{ background: "#FDE9EC" }}>
          <S.Big style={{ color: "#D6455D" }}>{lost}</S.Big>
          <S.Small><b>Не зайшли</b></S.Small>
          {lost > 0 && <S.Small>натисни — побачиш хто і чому</S.Small>}
        </S.Step>
      </S.Steps>
    </S.Card>
  );
}

function Sources({ bySource }) {
  const rows = Object.entries(bySource).sort((a, b) => b[1].total - a[1].total);
  return (
    <S.Card>
      <S.CardHead><div><S.H>Звідки приходять</S.H><S.Hint>Скільки написали → записались на пробний → прийшли → стали учнями.</S.Hint></div></S.CardHead>
      {rows.length === 0 ? <S.Empty>За період ще ніхто не писав</S.Empty> : (
        <S.Table>
          <table>
            <thead><tr><th>Звідки</th><th>Написали</th><th>Пробний</th><th>Прийшли</th><th>Учні</th><th>Пропали</th></tr></thead>
            <tbody>
              {rows.map(([k, r]) => (
                <tr key={k}>
                  <td><b>{SOURCE_LABEL[k] || "Не вказано"}</b></td>
                  <td>{r.total}</td><td>{r.trial}</td><td>{r.attended}</td>
                  <td><b>{r.joined}</b> <S.Small as="span">{pct(r.joined, r.total)}</S.Small></td>
                  <td>{r.lost}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </S.Table>
      )}
    </S.Card>
  );
}

function LostReasons({ reasons, leads }) {
  // Старі записи мають причину вільним текстом — на графіку це «Інше», а сам текст — у списку нижче
  const grouped = {};
  Object.entries(reasons).forEach(([k, n]) => { const key = LOST_REASONS.includes(k) || k === "не вказано" ? k : "Інше"; grouped[key] = (grouped[key] || 0) + n; });
  const rows = Object.entries(grouped).sort((a, b) => b[1] - a[1]);
  const total = rows.reduce((s, [, n]) => s + n, 0);
  const notes = leads.filter((l) => l.stage === "lost" && (l.lostNote || (l.lostReason && !LOST_REASONS.includes(l.lostReason))))
    .map((l) => ({ ...l, lostNote: [LOST_REASONS.includes(l.lostReason) ? "" : l.lostReason, l.lostNote].filter(Boolean).join(" — ") }));
  return (
    <S.Card>
      <S.CardHead><div><S.H>Чому пропадають</S.H><S.Hint>Причини тих, хто написав за період і пропав.</S.Hint></div></S.CardHead>
      {rows.length === 0 ? <S.Empty>Ніхто не пропав</S.Empty> : (
        <S.Bars>
          {rows.map(([k, n]) => (
            <S.BarRow key={k} style={{ gridTemplateColumns: "170px 1fr 40px" }}>
              <span>{k}</span>
              <S.BarTrack><i style={{ width: `${(n / Math.max(total, 1)) * 100}%` }} /></S.BarTrack>
              <b>{n}</b>
            </S.BarRow>
          ))}
        </S.Bars>
      )}
      {notes.length > 0 && (
        <S.List style={{ marginTop: 12 }}>
          {notes.map((l) => <li key={l._id}><span><b>{l.name}</b> · {l.lostNote}</span><S.Small as="span">{STEP_SHORT[l.reached]}</S.Small></li>)}
        </S.List>
      )}
    </S.Card>
  );
}

function Leads({ leads, filter, setFilter, onChange }) {
  const [open, setOpen] = useState(false);
  const shown = useMemo(() => leads.filter((l) => {
    if (filter === "all") return true;
    if (filter === "active") return !["paid", "lost"].includes(l.stage || "new");
    if (filter.startsWith("lost:")) return l.stage === "lost";
    return (l.stage || "new") === filter;
  }), [leads, filter]);

  return (
    <S.Card id="funnel-people">
      <S.CardHead>
        <div>
          <S.H>Люди</S.H>
          <S.Hint>Сайт, бот і пробні з «Пробні уроки» додаються самі. Написали в директ — «+ Людина». Далі одним тапом: Пробний → Навчається, або «Не зайшов».</S.Hint>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <S.Select value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Фільтр">
            <option value="active">В роботі</option>
            <option value="paid">Навчаються</option>
            <option value="lost:any">Не зайшли</option>
            <option value="all">Усі</option>
          </S.Select>
          <S.Btn onClick={() => setOpen(!open)}>{open ? "Закрити" : "+ Людина"}</S.Btn>
        </div>
      </S.CardHead>
      {open && <AddLead onDone={() => { setOpen(false); onChange(); }} />}
      {shown.length === 0 ? <S.Empty>Нікого в цьому фільтрі</S.Empty> : (
        <S.People>{shown.map((l) => <PersonRow key={l._id} lead={l} onChange={onChange} />)}</S.People>
      )}
    </S.Card>
  );
}

function AddLead({ onDone }) {
  const [form, setForm] = useState({ name: "", phone: "", source: "", stage: "new", referredBy: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k, v) => setForm({ ...form, [k]: v });
  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setErr("Вкажи імʼя або нік");
    if (!form.source) return setErr("Обери, звідки написали");
    setBusy(true); setErr("");
    axios.post("admin/leads", form).then(onDone).catch(() => setErr("Не вдалося зберегти")).finally(() => setBusy(false));
  };
  return (
    <S.FormBox onSubmit={submit}>
      <S.FormRow>
        <label>Імʼя / нік*<S.Input value={form.name} onChange={(e) => set("name", e.target.value)} autoFocus /></label>
        <label>Телефон / нік<S.Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+380… або @nick" /></label>
        <label>Нотатка<S.Input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="ціль, вік, зручний час" /></label>
      </S.FormRow>
      <label>Звідки*
        <S.Chips>{Object.entries(SOURCE_LABEL).map(([k, v]) => <S.Chip key={k} type="button" $on={form.source === k} onClick={() => set("source", k)}>{v}</S.Chip>)}</S.Chips>
      </label>
      {form.source === "referral" && <label>Хто порадив<S.Input value={form.referredBy} onChange={(e) => set("referredBy", e.target.value)} /></label>}
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <S.Btn type="submit" disabled={busy}>{busy ? "Зберігаю…" : "Додати"}</S.Btn>
        {err && <S.ErrorText>{err}</S.ErrorText>}
      </div>
    </S.FormBox>
  );
}

// Рядок людини: етапи кнопками, «Пропав» з причиною, джерело (якщо не вказане)
function PersonRow({ lead: l, why, onChange }) {
  const [lostOpen, setLostOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const stage = l.stage || "new";
  const lost = stage === "lost";
  const simple = simpleOf(stage);
  const simpleIdx = SIMPLE.findIndex((x) => x.id === simple);

  const patch = (body) => {
    setBusy(true);
    return axios.patch(`admin/leads/${l._id}`, body).then(onChange).catch(() => alert("Не вдалося оновити")).finally(() => setBusy(false));
  };
  const saveLost = () => {
    if (!reason && !note.trim()) return alert("Обери причину або напиши своїми словами");
    patch({ stage: "lost", lostReason: reason || "Інше", lostNote: note.trim() }).then(() => setLostOpen(false));
  };

  return (
    <S.Person>
      <S.PersonHead>
        <span>
          <b>{l.name}</b>{l.phone ? ` · ${l.phone}` : ""} · {SOURCE_LABEL[l.source] || "звідки — не вказано"}{l.referredBy ? ` (від: ${l.referredBy})` : ""}
          <S.Hint style={{ margin: 0 }}>
            написав {day(l.createdAt)}{l.lastMoveAt && stage !== "new" ? ` · останній крок ${daysAgo(l.lastMoveAt) === 0 ? "сьогодні" : `${daysAgo(l.lastMoveAt)} дн. тому`}` : ""}
            {l.notes ? ` · ${l.notes}` : ""}
          </S.Hint>
        </span>
        <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {why && <S.Tag $c={WHY_LABEL[why][1]}>{WHY_LABEL[why][0]}</S.Tag>}
          {l.trialStatus && <S.Tag $c={TRIAL_LABEL[l.trialStatus]?.[1]}>{TRIAL_LABEL[l.trialStatus]?.[0] || l.trialStatus}</S.Tag>}
        </span>
      </S.PersonHead>

      {!l.source && (
        <S.Chips>
          <S.Small as="span" style={{ alignSelf: "center" }}>Звідки:</S.Small>
          {Object.entries(SOURCE_LABEL).map(([k, v]) => <S.Chip key={k} type="button" disabled={busy} onClick={() => patch({ source: k })}>{v}</S.Chip>)}
        </S.Chips>
      )}

      {lost ? (
        <S.Chips style={{ alignItems: "center" }}>
          <S.Tag $c="red">Не зайшов{l.reached && l.reached !== "new" ? ` (після: ${STEP_LABEL[l.reached]})` : ""}</S.Tag>
          <S.Small as="span">{[l.lostReason, l.lostNote].filter(Boolean).join(" — ")}</S.Small>
          <S.Chip type="button" disabled={busy} onClick={() => patch({ stage: l.reached })}>Повернувся</S.Chip>
        </S.Chips>
      ) : (
        <S.Chips>
          {SIMPLE.map((x, i) => (
            <S.Chip key={x.id} type="button" disabled={busy} $on={x.id === simple} $done={i < simpleIdx} onClick={() => x.id !== simple && patch({ stage: x.id })}>
              {i < simpleIdx ? "✓ " : ""}{x.id === "paid" ? "✅ " : ""}{x.label}
            </S.Chip>
          ))}
          {stage !== "paid" && <S.LostChip type="button" disabled={busy} $on={lostOpen} onClick={() => setLostOpen(!lostOpen)}>❌ Не зайшов</S.LostChip>}
        </S.Chips>
      )}

      {lostOpen && !lost && (
        <S.Panel>
          <S.Small><b>Чому не зайшов?</b> Обери причину, за бажанням — допиши своїми словами.</S.Small>
          <S.Chips>{LOST_REASONS.map((r) => <S.LostChip key={r} type="button" $on={reason === r} onClick={() => setReason(reason === r ? "" : r)}>{r}</S.LostChip>)}</S.Chips>
          <S.Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Своїми словами: напр. «все ок, сказав подумає — і тиша»" maxLength={300} />
          <div style={{ display: "flex", gap: 8 }}>
            <S.Btn type="button" disabled={busy} onClick={saveLost} style={{ background: "#D6455D" }}>Зберегти</S.Btn>
            <S.Btn type="button" $ghost onClick={() => setLostOpen(false)}>Скасувати</S.Btn>
          </div>
        </S.Panel>
      )}
    </S.Person>
  );
}

function Retention({ r }) {
  return (
    <S.Card>
      <S.CardHead>
        <div>
          <S.H>Продовження</S.H>
          <S.Hint>Напиши цим учням про продовження, поки уроки не закінчились. Активних: {r.active}, на паузі: {r.paused}.</S.Hint>
        </div>
      </S.CardHead>
      <S.Small style={{ fontWeight: 700, margin: "4px 0" }}>Залишилось 1–2 уроки ({r.lowBalance.length})</S.Small>
      {r.lowBalance.length === 0 ? <S.Empty>Немає</S.Empty> : (
        <S.List>{r.lowBalance.map((s) => <li key={s._id}><span><b>{withId(s.name, s.studentId)}</b> · {withId(s.teacherName, s.teacherCode)}</span><S.Tag $c="amber">{s.totalLessons} ур.</S.Tag></li>)}</S.List>
      )}
      <S.Small style={{ fontWeight: 700, margin: "12px 0 4px" }}>0 уроків або борг ({r.zeroOrDebt.length})</S.Small>
      {r.zeroOrDebt.length === 0 ? <S.Empty>Немає</S.Empty> : (
        <S.List>{r.zeroOrDebt.map((s) => <li key={s._id}><span><b>{withId(s.name, s.studentId)}</b> · {withId(s.teacherName, s.teacherCode)}</span><S.Tag $c="red">{s.totalLessons} ур.</S.Tag></li>)}</S.List>
      )}
    </S.Card>
  );
}

function Referrals({ items, onChange }) {
  const [busy, setBusy] = useState(null);
  const reward = (r) => {
    setBusy(r._id);
    axios.post(`admin/leads/${r._id}/referral-reward`)
      .then(onChange)
      .catch((e) => alert(e?.response?.data?.message || "Не вдалося нарахувати"))
      .finally(() => setBusy(null));
  };
  const markManual = (r) => {
    if (!window.confirm("Позначити, що +2 уроки обом нараховано вручну?")) return;
    axios.patch(`admin/leads/${r._id}/referral-manual`).then(onChange);
  };
  return (
    <S.Card>
      <S.CardHead>
        <div>
          <S.H>«Приведи друга»</S.H>
          <S.Hint>Бонус +2 уроки кожному — тільки коли друг став учнем і оплатив перший пакет. Учні діляться своїм посиланням з кабінету.</S.Hint>
        </div>
      </S.CardHead>
      {items.length === 0 ? <S.Empty>За період рекомендацій ще немає</S.Empty> : (
        <S.List>
          {items.map((r) => (
            <li key={r._id}>
              <span><b>{r.name}</b> ← {r.referredBy || "хто — не вказано"}</span>
              {r.referralRewarded ? <S.Tag $c="green">нараховано ✓</S.Tag>
                : r.stage !== "paid" ? <S.Tag>{r.stage === "lost" ? "Пропав" : STEP_LABEL[r.stage || "new"]}</S.Tag>
                : r.autoReward ? <S.Btn onClick={() => reward(r)} disabled={busy === r._id}>{busy === r._id ? "…" : "Нарахувати +2 обом"}</S.Btn>
                : <S.Btn $ghost onClick={() => markManual(r)}>Нараховано вручну</S.Btn>}
            </li>
          ))}
        </S.List>
      )}
    </S.Card>
  );
}

function Cancellations({ c, items, students, onChange }) {
  const empty = { studentId: "", by: "teacher", reason: "", lessonDate: iso(new Date()), replaced: false };
  const [form, setForm] = useState(empty);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return students.filter((s) => s.name.toLowerCase().includes(q) || String(s.studentId).includes(q)).slice(0, 6);
  }, [query, students]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.studentId) return setErr("Обери учня зі списку");
    setBusy(true); setErr("");
    axios.post("admin/cancellations", form)
      .then(() => { setForm(empty); setQuery(""); setOpen(false); onChange(); })
      .catch(() => setErr("Не вдалося зберегти"))
      .finally(() => setBusy(false));
  };
  const remove = (id) => {
    if (!window.confirm("Видалити цей запис?")) return;
    axios.delete(`admin/cancellations/${id}`).then(onChange);
  };

  return (
    <S.Card>
      <S.CardHead>
        <div>
          <S.H>Відміни уроків</S.H>
          <S.Hint>З платформи записуються самі. Скасували в особистих — внеси тут. Якщо скасовує викладач — заміну шукає школа.</S.Hint>
        </div>
        <S.Btn onClick={() => setOpen(!open)}>{open ? "Закрити" : "+ Відміна"}</S.Btn>
      </S.CardHead>

      {open && (
        <S.Form onSubmit={submit}>
          <label style={{ position: "relative" }}>Учень*
            <S.Input value={query} onChange={(e) => { setQuery(e.target.value); setForm({ ...form, studentId: "" }); }} placeholder="імʼя або ID" />
            {matches.length > 0 && !form.studentId && (
              <S.List style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 5, background: "#fff", border: "1px solid #E7E5F5", borderRadius: 10, padding: "0 8px" }}>
                {matches.map((s) => (
                  <li key={s._id} style={{ cursor: "pointer" }} onClick={() => { setForm({ ...form, studentId: s._id }); setQuery(`${s.name} (ID ${s.studentId})`); }}>
                    <span>{s.name} (ID {s.studentId})</span><span>{s.teacherName}</span>
                  </li>
                ))}
              </S.List>
            )}
          </label>
          <label>Хто скасував
            <S.Select value={form.by} onChange={(e) => setForm({ ...form, by: e.target.value })}>
              <option value="teacher">Викладач</option>
              <option value="student">Учень</option>
              <option value="school">Школа</option>
            </S.Select>
          </label>
          <label>Дата уроку<S.Input type="date" value={form.lessonDate} onChange={(e) => setForm({ ...form, lessonDate: e.target.value })} /></label>
          <label>Причина<S.Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="захворів, робота…" /></label>
          {form.by === "teacher" && (
            <label style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
              <input type="checkbox" style={{ appearance: "auto", WebkitAppearance: "auto" }} checked={form.replaced} onChange={(e) => setForm({ ...form, replaced: e.target.checked })} /> Знайшли заміну
            </label>
          )}
          <S.Btn type="submit" disabled={busy}>{busy ? "Зберігаю…" : "Додати"}</S.Btn>
          {err && <S.ErrorText>{err}</S.ErrorText>}
        </S.Form>
      )}

      <S.Stats style={{ marginBottom: 14 }}>
        <S.Stat><S.Big>{c.total}</S.Big><S.Small>відмін за період</S.Small></S.Stat>
        <S.Stat $tone="pink"><S.Big>{c.byTeacher}</S.Big><S.Small>скасували викладачі</S.Small></S.Stat>
        <S.Stat><S.Big>{c.byStudent}</S.Big><S.Small>скасували учні</S.Small></S.Stat>
        <S.Stat $tone="amber"><S.Big>{c.teacherReplaced}/{c.byTeacher}</S.Big><S.Small>відмін викладача із заміною</S.Small></S.Stat>
      </S.Stats>

      {c.perTeacher.some((t) => t.teacher > 0) && (
        <>
          <S.Small style={{ fontWeight: 700, marginBottom: 4 }}>Відміни з боку викладачів</S.Small>
          <S.List style={{ marginBottom: 14 }}>
            {c.perTeacher.filter((t) => t.teacher > 0).map((t) => (
              <li key={t.teacherName}><span>{t.teacherName}</span><S.Tag $c={t.teacher > 2 ? "red" : "amber"}>{t.teacher}</S.Tag></li>
            ))}
          </S.List>
        </>
      )}

      {items.length === 0 ? <S.Empty>Відмін за період немає</S.Empty> : (
        <S.Table>
          <table>
            <thead><tr><th>Внесено</th><th>Учень</th><th>Викладач</th><th>Хто скасував</th><th>Причина</th><th>Джерело</th><th></th></tr></thead>
            <tbody>
              {items.map((x) => (
                <tr key={x._id}>
                  <td>{day(x.createdAt)}</td>
                  <td>{withId(x.studentName, x.studentCode)}</td>
                  <td>{withId(x.teacherName, x.teacherCode)}</td>
                  <td><S.Tag $c={x.by === "teacher" ? "pink" : undefined}>{BY_LABEL[x.by]}</S.Tag>{x.by === "teacher" && x.replaced ? <S.Hint>заміну знайдено</S.Hint> : null}</td>
                  <td>{x.reason || "—"}</td>
                  <td>{x.source === "platform" ? "платформа" : "вручну"}</td>
                  <td>{x.source === "manual" && <S.Btn $ghost onClick={() => remove(x._id)} aria-label="Видалити">✕</S.Btn>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </S.Table>
      )}
    </S.Card>
  );
}

export default Funnel;
